import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const axios = require('axios').default;
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Flatpickr from "react-flatpickr";

import {
  setGonTimeZoneFromVenue
} from '../../../shared/timeZoneLogic';
import RoomSelector from '../../../shared/RoomSelector';
import PdfDropzone from '../../../shared/PdfDropzone';
import useVenueAgeRestrictionOptions from '../../shared/hooks/useVenueAgeRestrictionOptions';
import ArtistPickerField from '../../shared/components/ArtistPickerField';
import { readLastOfferLayout, writeLastOfferLayout } from '../utils/offerLayoutPreference';
import { applyDefaultStart } from '../../../shared/eventTimeDefaults';

const MODE_EXISTING_EVENT = 'existing';
const MODE_NEW_EVENT = 'new';
const MODE_UPLOAD_PDF = 'upload';

const canSubmitForm = (offer, mode) => {
  if (mode === MODE_UPLOAD_PDF) {
    return false;
  }
  if (mode === MODE_EXISTING_EVENT) {
    return offer.calendar_event_id;
  }
  return (
    offer.room_ids && offer.room_ids.length > 0
      && offer.venue_id
      && offer.event_name
      && offer.event_name.length > 0
  );
};

const selectStyles = {
  container: (base, state) => ({
    ...base,
    opacity: state.isDisabled ? ".5" : "1",
    backgroundColor: "transparent",
    zIndex: "999"
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: provided => ({
    ...provided,
    zIndex: "9999 !important",
    backgroundColor: "var(--od-surface, #fff)",
    border: "1px solid var(--od-border, #d5d7da)"
  }),
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--od-surface, #fff)",
    borderColor: state.isFocused ? "var(--od-accent, #1982c4)" : "var(--od-border, #d5d7da)",
    boxShadow: "none"
  }),
  singleValue: base => ({ ...base, color: "var(--od-text, #181d27)" }),
  input: base => ({ ...base, color: "var(--od-text, #181d27)" }),
  placeholder: base => ({ ...base, color: "var(--od-muted, #717680)" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--od-accent-subtle, #eff6fb)"
      : state.isFocused
        ? "var(--od-surface-2, #f5f5f5)"
        : "var(--od-surface, #fff)",
    color: "var(--od-text, #181d27)"
  }),
  indicatorSeparator: base => ({ ...base, backgroundColor: "var(--od-border, #d5d7da)" })
};

const debouncedConfirmFetch = _.debounce((inputValue, callback, team) => {
  new Promise(resolve => {
    axios.get('/teams/' + team.id + '/calendar_events/autocomplete', {
        params: {
          name: inputValue
        }
      })
      .then(function (response) {
        var options = response.data.map(function(data){
          return { value: data.id, label: data.name };
        });

        callback(options);
      })
  });
}, 250);

const debouncedFetchEventTemplates = _.debounce((team, inputValue, callback) => {
  new Promise(resolve => {
    axios.get(`/teams/${team.id}/event_templates`, {params: {q: inputValue}}).then(function (response) {
      var newOptions = response.data.map(function(data){
        return { value: data.id, label: data.name };
      });

      callback(newOptions);
    })
  });
}, 250);

const performerIsDisabled = (offer, performer) => {
  const hasV2Offer = performer.offer && performer.offer.id && performer.offer.id !== offer.id;
  const hasV3Offer = performer.v3_offer && performer.v3_offer.id;
  return hasV2Offer || hasV3Offer;
};

const performerIsSelected = (offer, performer) => {
  return (offer.performer_id === performer.id);
};

const OfferV2Modal = ({
  csrfToken,
  team,
  currentUser,
  venuesOptions,
  onClose,
  offer,
  closeModal,
  createCalendarEventChanged,
  eventNameChanged,
  eventTemplateChanged,
  venueIdChanged,
  venueSelected,
  startTimeChanged,
  primaryArtistChanged,
  calendarEventChanged,
  fetchCalendarEventPerformers,
  calendarEvent,
  performers,
  performerIdChanged,
  fetchArtistData,
  selectedArtist,
  clearSelectedArtist,
  createOffer,
  performersChanged,
  pipeline,
  venueOwnerships,
  rooms,
  selectedRooms,
  selectedRoomsChanged,
  ageRestrictionChanged,
  onUploadStarted,
  offerV3,
}) => {
  const [mode, setMode] = useState(MODE_EXISTING_EVENT);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // Which editor to open after creating a V3 offer: 'advanced' (full V3 builder)
  // or 'simple' (stripped-down editor). Only relevant for V3 offers. Seeded from
  // the user's last selection, falling back to 'simple'.
  const [layout, setLayout] = useState(readLastOfferLayout);

  const chooseLayout = (newLayout) => {
    setLayout(newLayout);
    writeLastOfferLayout(newLayout);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === MODE_NEW_EVENT) {
      createCalendarEventChanged(true);
    } else if (newMode === MODE_EXISTING_EVENT) {
      createCalendarEventChanged(false);
    }
  };

  const handleFilesSelected = (newFiles) => {
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);

    const formData = new FormData();
    const sourceCalendarEventId = offer.calendar_event_id || calendarEvent?.value || calendarEvent?.id;
    selectedFiles.forEach(fileData => {
      formData.append('files[]', fileData.file);
    });
    if (sourceCalendarEventId) {
      formData.append('calendar_event_id', sourceCalendarEventId);
    }

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.post(`/teams/${team.id}/document_processing_jobs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Upload started. Processing your PDF...', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
        hideProgressBar: true
      });

      if (onUploadStarted) {
        onUploadStarted();
      }

      closeModal(onClose);
    } catch (error) {
      setIsUploading(false);
      const errorMessage = error.response?.data?.join?.(', ') || 'Upload failed. Please try again.';
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 5000,
        hideProgressBar: true
      });
    }
  };

  useEffect(() => {
    if(Object.keys(offer).length === 0){
      createCalendarEventChanged(false);
    }

    if(calendarEvent && calendarEvent.id){
      calendarEventChanged(calendarEvent);
    }

    // calendarEventChanged resets performers
    if(performers && performers.length > 0){
      performersChanged(performers);

      // Pre-select the first selectable artist so an offer launched from an
      // event with a single performer doesn't require an extra click.
      const firstSelectable = performers.find(p => !performerIsDisabled(offer, p));
      if(firstSelectable){
        performerIdChanged(firstSelectable.id);
      }
    }

    if(selectedArtist && selectedArtist.id){
      primaryArtistChanged({
        label: selectedArtist.name,
        value: selectedArtist.id
      });

      fetchArtistData(selectedArtist.permalink);
    }

    // Seed the "New event" fields from the source confirm so the artist and
    // event name carry over. primaryArtistChanged sets event_name to the artist
    // label as a side effect, so dispatch it before eventNameChanged.
    const seedPerformer = (performers || []).find(p => p.artistable_type === "Artist" && p.artistable);
    if(seedPerformer){
      primaryArtistChanged({
        label: seedPerformer.artistable.name,
        value: seedPerformer.artistable.id,
        permalink: seedPerformer.artistable.permalink
      });
      fetchArtistData(seedPerformer.artistable.permalink);
    }
    if(calendarEvent && calendarEvent.title){
      eventNameChanged(calendarEvent.title);
    }
  }, []);

  useEffect(() => {
    if(selectedRooms && selectedRooms.length > 0) {
      const firstRoom = selectedRooms[0];
      const venueOwnership = venueOwnerships.find(vo => vo.id === firstRoom.venueOwnershipId);
      if (venueOwnership) {
        venueIdChanged(venueOwnership.venueable_id);
        setGonTimeZoneFromVenue(venueOwnerships, venueOwnership.venueable_id);
      }
    }
  }, [selectedRooms, venueOwnerships]);

  const ageRestrictionVenueOwnershipId = (selectedRooms && selectedRooms[0]?.venueOwnershipId) || null;
  const { options: ageRestrictionOptions, defaultValue: ageRestrictionDefault } =
    useVenueAgeRestrictionOptions(team?.id, ageRestrictionVenueOwnershipId);

  useEffect(() => {
    if (mode !== MODE_NEW_EVENT) return;
    if (!ageRestrictionDefault) return;
    if (offer.age_restriction) return;
    ageRestrictionChanged(ageRestrictionDefault);
    // Deps intentionally narrow: fire only when the venue default changes, not on every offer edit.
  }, [ageRestrictionDefault, mode]);

  // When the modal is launched from a Hold, the "Existing event" option is
  // really "the Hold this offer is for" — relabel it and surface the hold
  // dates so the booker sees what they're attaching to.
  const sourceEvent = calendarEvent || {};
  const sourceIsHold = sourceEvent.type === 'Hold';
  const sourceHoldDates = sourceEvent.holdDates || [];
  const hasSourceEvent = Boolean(sourceEvent.id || sourceEvent.value);
  const existingEventLabel = sourceIsHold ? 'Existing Hold(s)' : 'Existing event';

  return (
    <div className="offer-modal-container"
         style={{"padding": "30px 40px"}}>
      <div className="row">
        <div className="col">
          <h3 style={{"fontSize": "20px"}}>
            <strong>Create Offer</strong>
          </h3>
        </div>
        <div className="col-xs-auto"
             style={{"paddingRight": "15px"}}>
          <a href="#"
             onClick={
               (e) => {
                 e.preventDefault();
                 closeModal(onClose);
               }
             }
             style={{"fontSize": "26px"}}
             className="text-dark">
            <i className="fal fa-times"></i>
          </a>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <p className="text-muted"
             style={{"fontSize": "16px"}}>
            {team.package?.ai_features
              ? "Build your offer for an existing event, create a brand new event, or upload a PDF."
              : "Build your offer for an existing event or create a brand new event."}
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <form onSubmit={
                  (e) => {
                    e.preventDefault();

                    if(!canSubmitForm(offer, mode)){
                      return false;
                    }

                    createOffer(csrfToken, team, offer, pipeline, offerV3, layout);
                  }
                }>
            <div className="form-row my-3">
              <div className="col-12">
                <div className="custom-control custom-radio custom-control-inline">
                  <input type="radio"
                         id="offer-mode-existing"
                         checked={mode === MODE_EXISTING_EVENT}
                         onChange={() => handleModeChange(MODE_EXISTING_EVENT)}
                         className="custom-control-input" />
                  <label className="custom-control-label"
                         style={{"fontSize": "16px"}}
                         htmlFor="offer-mode-existing">
                    {existingEventLabel}
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input type="radio"
                         id="offer-mode-new"
                         checked={mode === MODE_NEW_EVENT}
                         onChange={() => handleModeChange(MODE_NEW_EVENT)}
                         className="custom-control-input" />
                  <label className="custom-control-label"
                         style={{"fontSize": "16px"}}
                         htmlFor="offer-mode-new">
                    New event
                  </label>
                </div>
                {team.package?.ai_features && (
                  <div className="custom-control custom-radio custom-control-inline">
                    <input type="radio"
                           id="offer-mode-upload"
                           checked={mode === MODE_UPLOAD_PDF}
                           onChange={() => handleModeChange(MODE_UPLOAD_PDF)}
                           className="custom-control-input" />
                    <label className="custom-control-label"
                           style={{"fontSize": "16px"}}
                           htmlFor="offer-mode-upload">
                      Upload PDF
                    </label>
                  </div>
                )}
              </div>
            </div>
            {mode === MODE_UPLOAD_PDF && (
              <div className="row">
                <div className="col-12">
                  <PdfDropzone
                    selectedFiles={selectedFiles}
                    onFilesSelected={handleFilesSelected}
                    onRemoveFile={handleRemoveFile}
                    onUpload={handleUpload}
                    onCancel={() => setSelectedFiles([])}
                    isUploading={isUploading}
                  />
                </div>
              </div>
            )}
            {mode === MODE_NEW_EVENT && hasSourceEvent && (
              <div className="row mb-2">
                <div className="col-12">
                  <div className="alert alert-warning mb-0" role="alert" data-testid="new-event-alert">
                    <i className="far fa-exclamation-triangle mr-2" aria-hidden="true" />
                    Creating a new event won't attach this offer to
                    {sourceEvent.title ? ` "${sourceEvent.title}"` : ' the current hold'}
                    {sourceIsHold ? ' or its hold dates.' : '.'}
                    {' '}Use {existingEventLabel} to keep them linked.
                  </div>
                </div>
              </div>
            )}
            {mode === MODE_NEW_EVENT && (
              <div className="row">
                <div className="col-12">
                  <div className="form-row">
                    <div className="col-8">
                      <div className="form-group">
                        <label htmlFor="offer-rooms"
                               className="text-muted">
                          Venue
                        </label>
                        <RoomSelector
                          rooms={rooms}
                          venueOwnerships={venueOwnerships}
                          selectedRooms={selectedRooms || []}
                          onChange={selectedRoomsChanged}
                          placeholder="Select venue..."
                          menuPortalTarget={document.querySelector('body')}
                          id="offer-rooms"
                        />
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="form-group">
                        <label htmlFor="offer-date-time"
                               className="text-muted">
                          Date/Time (leave blank for TBD dates)
                        </label>
                        <Flatpickr
                          data-enable-time
                          options={{
                            dateFormat: "F j, Y - h:i K"
                          }}
                          placeholder={"Select date and time"}
                          style={{borderWidth: "1px"}}
                          className={"form-control form-control-flatpickr"}
                          value={offer.start_time || ""}
                          onChange={(dates, _dateText, instance) => {
                            if (dates.length === 0) {
                              instance.startDefaulted = false;
                              return;
                            }
                            if (instance.startDefaulted || (offer.start_time && offer.start_time.length)) return;
                            instance.startDefaulted = true;
                            instance.setDate(applyDefaultStart(team.event_time_defaults, dates[0]), false);
                          }}
                          onClose={date => {
                            startTimeChanged(date);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    {Object.keys(selectedArtist).length === 0 ? (
                      <div>
                        <label htmlFor="offer-primary-artist" className="text-muted">
                          Artist (optional)
                        </label>
                        <ArtistPickerField
                          id="offer-primary-artist"
                          value={offer.primary_artist}
                          placeholder="Search artists..."
                          teamId={team.id}
                          onSelect={
                            ({ artist }) => {
                              primaryArtistChanged({
                                value: artist.id,
                                label: artist.name,
                                permalink: artist.permalink
                              });
                              fetchArtistData(artist.permalink);
                            }
                          } />
                      </div>
                    ) : (
                      <div className={"col-12 p-2 mt-2 selectable-card selected"}>
                        <div className="row align-items-center">
                          <div className="col-xs-auto d-flex" style={{'paddingLeft': '20px'}}>
                            <div className="custom-control custom-radio align-self-center">
                              <input type="radio"
                                     checked={true}
                                     onChange={() => {}}
                                     id={"offer-selected-artist"}
                                     className="custom-control-input" />
                              <label className="custom-control-label"
                                     htmlFor={"offer-selected-artist"}>
                                &nbsp;
                              </label>
                            </div>
                          </div>
                          <div className="col-xs-auto" style={{'paddingLeft': '15px'}}>
                            <img src={selectedArtist.image_thumb_url_for_json}
                                 className="img-fluid rounded-circle mr-2 less-opacity-when-disabled"
                                 style={{"height": "60px", "width": "60px"}} />
                          </div>
                          <div className="col less-opacity-when-disabled">
                            <h5 className="mb-0">
                              <strong>
                                {selectedArtist.name}
                              </strong>
                            </h5>
                          </div>
                          <div className="col-xs-auto" style={{'paddingRight': '15px'}}>
                            <a href='#'
                               className='mt-3 d-inline-block'
                               onClick={
                                 (e) => {
                                   e.preventDefault();
                                   clearSelectedArtist();
                                 }
                               }
                               style={{'color': '#1982C4'}}
                               >
                              <i className="fa fa-times-circle"></i>
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="offer-event-name"
                           className="text-muted">
                      Event Name
                    </label>
                    <input type="text"
                           className="form-control"
                           style={{borderWidth: "1px"}}
                           id="offer-event-name"
                           onChange={
                             (e) => {
                               eventNameChanged(e.target.value);
                             }
                           }
                           value={offer.event_name || ""}
                           placeholder="Enter event name" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-event-template" className="text-muted">
                      Event Template
                      <span className="text-optional pl-2">
                        (optional)
                      </span>
                    </label>
                    <AsyncSelect
                      cacheOptions
                      styles={selectStyles}
                      menuPortalTarget={document.querySelector('body')}
                      defaultOptions
                      id="new-event-template"
                      value={offer.event_template ? offer.event_template : null}
                      loadOptions={(inputValue, callback) => debouncedFetchEventTemplates(team, inputValue, callback)}
                      onChange={(option) => {
                        eventTemplateChanged(option);
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label
                      htmlFor="offer-event-name"
                      className="text-muted"
                    >
                      Age Restriction
                      <span className="text-optional pl-2">
                        (optional)
                      </span>
                    </label>
                    <Select
                      id="offer-age-restriction"
                      styles={selectStyles}
                      menuPortalTarget={document.querySelector('body')}
                      options={ageRestrictionOptions}
                      value={offer.age_restriction ? { value: offer.age_restriction, label: offer.age_restriction } : null}
                      onChange={(selected) => ageRestrictionChanged(selected?.value)}
                    />
                  </div>
                </div>
              </div>
            )}
            {mode === MODE_EXISTING_EVENT && (
              <div className="row">
                <div className="col-12">
                  <AsyncSelect cacheOptions
                               placeholder={"Select event"}
                               isClearable={true}
                               styles={selectStyles}
                               menuPortalTarget={document.querySelector('body')}
                               defaultOptions
                               id="offer-primary-artist"
                               onChange={
                                 (option) => {
                                   calendarEventChanged(option);

                                   if(option && option.value){
                                     fetchCalendarEventPerformers(team, option.value);
                                   }
                                 }
                               }
                               value={Object.keys(calendarEvent).length > 0 ? calendarEvent : ""}
                               loadOptions={(inputValue, callback) => debouncedConfirmFetch(inputValue, callback, team)} />
                </div>
                {sourceIsHold && sourceHoldDates.length > 0 && (
                  <div className="col-12 mt-2" data-testid="source-hold-dates">
                    <label className="text-muted mb-1 d-block">
                      HOLD {sourceHoldDates.length > 1 ? 'DATES' : 'DATE'}
                    </label>
                    {sourceHoldDates.map((hold) => (
                      <span key={hold.position}
                            className="badge badge-light mr-2 mb-1"
                            style={{"fontSize": "13px"}}>
                        H{hold.position} · {hold.date}
                      </span>
                    ))}
                  </div>
                )}
                <div className="col-12 mt-3">
                  {performers.length > 0 ? (
                    <label className="text-muted mb-0">
                      CHOOSE AN ARTIST
                    </label>
                  ) : null}
                  {performers.map((performer, index) => (
                    <div key={index}
                         onClick={
                           (e) => {
                             e.preventDefault();

                             if(!performerIsDisabled(offer, performer) && !performerIsSelected(offer, performer)){
                               performerIdChanged(performer.id);
                             }
                           }
                         }
                         className={"col-12 p-2 mt-2 selectable-card " + (performerIsSelected(offer, performer) ? "selected " : "") + (performerIsDisabled(offer, performer) ? "disabled " : "")}>
                      <div className="row">
                        <div className="col-xs-auto d-flex" style={{'paddingLeft': '20px'}}>
                          <div className="custom-control custom-radio align-self-center">
                            <input type="radio"
                                   checked={performerIsSelected(offer, performer)}
                                   onChange={() => {}}
                                   disabled={performerIsDisabled(offer, performer)}
                                   id={"partnership-" + index}
                                   name={"offer-partnership"}
                                   className="custom-control-input" />
                            <label className="custom-control-label"
                                   htmlFor={"partnership-" + index}>
                              &nbsp;
                            </label>
                          </div>
                        </div>
                        <div className="col-xs-auto" style={{'paddingLeft': '15px'}}>
                          <img src={performer.artistable.image_thumb_url_for_json}
                               className="img-fluid rounded-circle mr-2 less-opacity-when-disabled"
                               style={{"height": "60px", "width": "60px"}} />
                        </div>
                        <div className="col less-opacity-when-disabled">
                          <h5 className="mb-0">
                            <strong>
                              {performer.artistable.name}
                            </strong>
                          </h5>
                        </div>
                        {performerIsDisabled(offer, performer) ? (
                          <div className="col-xs-auto d-flex" style={{'paddingRight': '15px'}}>
                            <div className="align-self-center">
                              <span className="badge badge-secondary p-2" style={{"fontSize": "12px"}}>
                                <strong>Offer Exists</strong>
                              </span>
                            </div>
                          </div>
                        ) : null}
                        {performerIsSelected(offer, performer) ? (
                          <div className="col-xs-auto" style={{'paddingRight': '15px'}}>
                            <a href='#'
                               className='mt-3 d-inline-block'
                               onClick={
                                 (e) => {
                                   e.preventDefault();
                                   performerIdChanged(null);
                                 }
                               }
                               style={{'color': '#1982C4'}}
                               >
                              <i className="fa fa-times-circle"></i>
                            </a>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {offerV3 && mode !== MODE_UPLOAD_PDF && (
              <div className="form-row mt-4">
                <div className="col-12">
                  <label className="text-muted d-block mb-1">
                    Offer Layout
                  </label>
                  <div className="custom-control custom-radio custom-control-inline">
                    <input type="radio"
                           id="offer-layout-simple"
                           checked={layout === 'simple'}
                           onChange={() => chooseLayout('simple')}
                           className="custom-control-input" />
                    <label className="custom-control-label"
                           style={{"fontSize": "16px"}}
                           htmlFor="offer-layout-simple">
                      Simple
                    </label>
                  </div>
                  <div className="custom-control custom-radio custom-control-inline">
                    <input type="radio"
                           id="offer-layout-advanced"
                           checked={layout === 'advanced'}
                           onChange={() => chooseLayout('advanced')}
                           className="custom-control-input" />
                    <label className="custom-control-label"
                           style={{"fontSize": "16px"}}
                           htmlFor="offer-layout-advanced">
                      Advanced
                    </label>
                  </div>
                </div>
              </div>
            )}
            {mode !== MODE_UPLOAD_PDF && (
              <div className="row small" style={{"marginTop": "30px"}}>
                <div className="col-12 text-right">
                  <a href="#"
                     onClick={
                       (e) => {
                         e.preventDefault();
                         closeModal(onClose);
                       }
                     }>
                    <strong>Cancel</strong>
                  </a>
                  <input type="submit"
                         style={{"borderRadius": "8px"}}
                         className="btn btn-primary btn-sm ml-3 strong"
                         disabled={!canSubmitForm(offer, mode)}
                         value="Create" />
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

OfferV2Modal.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  venuesOptions: PropTypes.array,
  onClose: PropTypes.func,
  offer: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  createCalendarEventChanged: PropTypes.func.isRequired,
  eventNameChanged: PropTypes.func.isRequired,
  eventTemplateChanged: PropTypes.func.isRequired,
  venueIdChanged: PropTypes.func.isRequired,
  venueSelected: PropTypes.object,
  startTimeChanged: PropTypes.func.isRequired,
  primaryArtistChanged: PropTypes.func.isRequired,
  calendarEventChanged: PropTypes.func.isRequired,
  fetchCalendarEventPerformers: PropTypes.func.isRequired,
  calendarEvent: PropTypes.object,
  performers: PropTypes.array,
  performerIdChanged: PropTypes.func.isRequired,
  fetchArtistData: PropTypes.func.isRequired,
  selectedArtist: PropTypes.object,
  clearSelectedArtist: PropTypes.func.isRequired,
  createOffer: PropTypes.func.isRequired,
  performersChanged: PropTypes.func.isRequired,
  pipeline: PropTypes.object,
  venueOwnerships: PropTypes.array.isRequired,
  rooms: PropTypes.array.isRequired,
  selectedRooms: PropTypes.array,
  selectedRoomsChanged: PropTypes.func.isRequired,
  ageRestrictionChanged: PropTypes.func.isRequired,
  onUploadStarted: PropTypes.func,
  offerV3: PropTypes.bool,
};

export default OfferV2Modal;
